import { useCallback, useMemo, useState } from "react";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import type { SuggestedChipItemProps } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterSuggestionsBar";
import type { SuggestedIndustriesMap } from "app/pages/sales-intelligence/types";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import type { LegacyIndustryCategoriesById, LegacyIndustryFilterValueType } from "../models/LegacyIndustryFilter/types";
import type { SelectStrategy, SelectStrategyDeps } from "../types/industrySuggestions";
import { getSuggestions, makeFlatSelectStrategy } from "../utils/industrySuggestions";

const SUGGESTIONS_UPDATE_DELAY = 1500;

interface IUseIndustrySuggestionsProps {
    value: LegacyIndustryFilterValueType;
    industries: readonly IFlattenedCategory[];
    categoriesById: LegacyIndustryCategoriesById;
    ignoredIndustries: readonly string[];
    suggestedIndustries: SuggestedIndustriesMap;
    onFilterUpdate: (value: LegacyIndustryFilterValueType) => void;
    onIgnoreIndustries: (idsToIgnore: string[]) => void;
    inclusion: InclusionOption;
}

interface IUseIndustrySuggestionsReturnProps {
    suggestedIndustryChips: SuggestedChipItemProps[];
    selectStrategy: SelectStrategy;
    isLoading: boolean;
}

const useIndustrySuggestions = ({
    value,
    industries,
    categoriesById,
    ignoredIndustries,
    suggestedIndustries,
    onFilterUpdate,
    onIgnoreIndustries,
    inclusion,
}: IUseIndustrySuggestionsProps): IUseIndustrySuggestionsReturnProps => {
    const [isLoading, setIsLoading] = useState(false);

    const [suggestionIds, setSuggestionIds] = useState<readonly string[]>(() => {
        return getSuggestions(value.selectedIndustries, categoriesById, ignoredIndustries, suggestedIndustries);
    });

    useDidUpdate(() => {
        const updatedSuggestions = getSuggestions(
            value.selectedIndustries,
            categoriesById,
            ignoredIndustries,
            suggestedIndustries,
        );
        const withoutNewSuggestions = updatedSuggestions.filter((id) => suggestionIds.includes(id));
        const newSuggestions = updatedSuggestions.filter((id) => !suggestionIds.includes(id));

        setSuggestionIds(withoutNewSuggestions);
        setIsLoading(true);

        const timeout = setTimeout(() => {
            setIsLoading(false);
            setSuggestionIds(withoutNewSuggestions.concat(newSuggestions));
        }, SUGGESTIONS_UPDATE_DELAY);

        return () => clearTimeout(timeout);
    }, [value.selectedIndustries]);

    const onChipRemove = useCallback(
        (idsToRemove: string[]) => {
            const selectedIndustries = value.selectedIndustries.filter((id) => !idsToRemove.includes(id));

            onIgnoreIndustries(idsToRemove);

            onFilterUpdate({ ...value, selectedIndustries, inclusion });
        },
        [value, onIgnoreIndustries, onFilterUpdate, inclusion],
    );

    const selectStrategy = useMemo(() => {
        const selectStrategyDeps: SelectStrategyDeps = {
            value,
            industries,
            categoriesById,
            onFilterUpdate,
            onChipRemove,
            inclusion,
        };

        return makeFlatSelectStrategy(selectStrategyDeps);
    }, [value, industries, categoriesById, onFilterUpdate, onChipRemove, inclusion]);

    const suggestedIndustryChips = useMemo(() => {
        const handleApplySuggestion = (industry: IFlattenedCategory) => {
            onFilterUpdate({ ...value, selectedIndustries: value.selectedIndustries.concat(industry.id), inclusion });
        };

        return suggestionIds.reduce<SuggestedChipItemProps[]>((chips, id) => {
            const industry = categoriesById[id];

            if (!industry) {
                return chips;
            }

            chips.push({
                id: industry.id,
                text: industry.text,
                onApply: () => handleApplySuggestion(industry),
            });

            return chips;
        }, []);
    }, [suggestionIds, onFilterUpdate, value, inclusion, categoriesById]);

    return {
        suggestedIndustryChips,
        selectStrategy,
        isLoading,
    };
};

export default useIndustrySuggestions;
